import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import moment from 'moment-timezone';

import Booking from 'models/booking';
import { TIME_FORMAT, DATE_FORMAT, ParkingPass as ParkingPassModel } from 'models/parking-passes';
import ThinNextArrow from 'components/svgs/icons/thin-next-arrow';

class ParkingPeriod extends React.Component {
  constructor(props) {
    super(props);
    moment.locale(props.locale);
  }
  arrivalLabel() {
    const { parkingPass } = this.props;

    if (parkingPass.arriveBy) {
      return <FormattedMessage id="parking-pass.park-between" defaultMessage="PARK BETWEEN" />;
    }

    return <FormattedMessage id="parking-pass.park-after" defaultMessage="PARK AFTER" />;
  }

  departureLabel() {
    const { parkingPass } = this.props;

    if (parkingPass.departAfter) {
      return <FormattedMessage id="parking-pass.exit-between" defaultMessage="EXIT BETWEEN" />;
    }

    return <FormattedMessage id="parking-pass.exit-before" defaultMessage="EXIT BEFORE" />;
  }

  arrivalTime() {
    const { parkingPass, locale } = this.props;
    const startTime = parkingPass.startTime.locale(locale);

    if (parkingPass.arriveBy) {
      const arriveBy = parkingPass.arriveBy.locale(locale);
      return (
        <div className="parking-time-range h3-sg print-h4-sg text-weight-bold">
          {startTime.format(TIME_FORMAT)}-{arriveBy.format(TIME_FORMAT)}
        </div>
      );
    }

    const classNames = cx({
      'parking-time': true,
      'h1-sg': true,
      'print-h2-sg': true,
      'text-weight-bold': true,
      'vertical-padding-one-third-lte-sm': !!parkingPass.departAfter,
    });

    return (
      <div className={classNames}>
        {startTime.format(TIME_FORMAT)}
      </div>
    );
  }

  departureTime() {
    const { parkingPass, locale } = this.props;
    const endTime = parkingPass.endTime.locale(locale);

    if (parkingPass.departAfter) {
      const departAfter = parkingPass.departAfter.locale(locale);
      return (
        <div className="parking-time-range h3-sg print-h4-sg text-weight-bold">
          {departAfter.format(TIME_FORMAT)}-{endTime.format(TIME_FORMAT)}
        </div>
      );
    }

    const classNames = cx({
      'parking-time': true,
      'h1-sg': true,
      'print-h2-sg': true,
      'text-weight-bold': true,
      'vertical-padding-one-third-lte-sm': !!parkingPass.arriveBy,
    });

    return (
      <div className={classNames}>
        {endTime.format(TIME_FORMAT)}
      </div>
    );
  }

  eventDeparture() {
    const { parkingPass } = this.props;
    if (parkingPass.hoursAfterEvent && parkingPass.showEventBuffer()) {
      return (
        <div className="event-buffer text-weight-medium h6-sg">
          <FormattedMessage
            id="parking-pass.event-departure"
            defaultMessage="OR {eventHours} HOURS AFTER YOUR EVENT ENDS"
            values={{ eventHours: parkingPass.hoursAfterEvent }}
          />
        </div>
      );
    }

    return null;
  }

  minParkingTime() {
    const { parkingPass } = this.props;

    if (parkingPass.minParkingHours) {
      return (
        <div className="row text-center text-color-dark-slate with-top-margin-2x">
          <div className="h6-sg text-weight-book">
            <FormattedMessage
              id="parking-pass.minimum-stay"
              defaultMessage="{minHours} hour minimum stay required"
              values={{ minHours: parkingPass.minParkingHours }}
            />
          </div>
        </div>
      );
    }

    return null;
  }

  renderTBDTime() {
    return <span className="parking-time-range h3-sg print-h4-sg text-weight-bold">TBD</span>;
  }

  renderStartTime() {
    const { booking, parkingPass, locale } = this.props;
    if (booking.hasTBDTimes) {
      return this.renderTBDTime();
    }

    return (
      <Fragment>
        { this.arrivalTime() }
        <div className="h6-sg text-weight-book">
          { parkingPass.startTime.locale(locale).format(DATE_FORMAT) }
        </div>
      </Fragment>
    );
  }

  renderEndTime() {
    const { booking, parkingPass, locale } = this.props;
    if (booking.hasTBDTimes) {
      return this.renderTBDTime();
    }

    return (
      <Fragment>
        { this.departureTime() }
        <div className="h6-sg text-weight-book">
          { parkingPass.endTime.locale(locale).format(DATE_FORMAT) }
        </div>
        { this.eventDeparture() }
      </Fragment>
    );
  }

  render() {
    const { parkingPass } = this.props;

    const arrivalClasses = cx({
      'col-md-3': true,
      'col-xs-4': true,
      'col-print-4': true,
      'col-xs-offset-1': true,
      'col-md-offset-2': true,
      'padding-0': true,
    });

    const departureClasses = cx({
      'col-md-3': true,
      'col-xs-4': true,
      'col-print-4': true,
      'padding-0': true,
    });

    const arrowClasses = cx({
      'col-xs-2': true,
      'time-range-arrow': true,
      'fill-dark-slate': true,
      'vertical-padding-one-third-lte-sm': !!(parkingPass.arriveBy || parkingPass.departAfter),
    });

    return (
      <div>
        <div className="row text-center text-color-dark-slate">
          <div className={arrivalClasses}>
            <div className="h6-sg text-weight-medium">{this.arrivalLabel()}</div>
            { this.renderStartTime() }
          </div>
          <div className={arrowClasses}><ThinNextArrow /></div>
          <div className={departureClasses}>
            <div className="h6-sg text-weight-medium">{this.departureLabel()}</div>
            { this.renderEndTime() }
          </div>
        </div>
        {this.minParkingTime()}
      </div>
    );
  }
}

ParkingPeriod.propTypes = {
  booking: PropTypes.instanceOf(Booking).isRequired,
  parkingPass: PropTypes.instanceOf(ParkingPassModel).isRequired,
  locale: PropTypes.string,
};
ParkingPeriod.defaultProps = {
  locale: 'en-us',
};
export default ParkingPeriod;
